import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, Shield, MessageCircle, Truck, CreditCard, MapPin, Heart, Gift, Star } from 'lucide-react';
import '../FAQ/FAQ.css';

const FAQ: React.FC = () => {
  const [openQuestion, setOpenQuestion] = useState<number | null>(0);

  const faqs = [
    {
      id: 1,
      icon: Users,
      question: "Who can use HomeCookt?",
      answer: "Anyone with extra time which can be converted into money! Whether you're hungry wanting to eat, love to cook & sell your food, love to walk/ride/drive to deliver food locally or wish to donate free food to the hungry in your local community, you're welcome!"
    },
    {
      id: 2,
      icon: Shield,
      question: "Is it safe?",
      answer: "Yes — we encourage you to order from or sell to people you know and trust. You can also meet your cook or delivery person during handover if you like. It's all about keeping it local and personal."
    },
    {
      id: 3,
      icon: MessageCircle,
      question: "Do I need to talk to anyone?",
      answer: "Nope. Orders are quick and automatic. Chat only if needed — like confirming pickup time."
    },
    {
      id: 4,
      icon: Truck,
      question: "What about delivery?",
      answer: "It's local and simple. Many meals are picked up by hand or delivered by someone nearby — often a neighbour. Freshly cooked homemade meals can be delivered either by someone who can walk, ride a bicycle/motor bike or drive. This will enable almost every adult who has some free time to deliver food locally or on their way to and from work."
    },
    {
      id: 5,
      icon: CreditCard,
      question: "How do I get paid (for cooks)?",
      answer: "You get paid cash on delivery, making you instantly have the cash in hand for food shopping. You cook again with the extra cash, sell it and make even more money. There are mothers including single mothers who make little extra food during their routine daily cooking who sell their food and make a living worth a full salary, if done consistently. The people who deliver can be tipped by the cooks at the collection points and by the buyers at the delivery points by cash. This way the local community is able to generate cash income on a daily basis, enriching one another."
    },
    {
      id: 6,
      icon: MapPin,
      question: "Can I join from any area?",
      answer: "Of course you can! We're growing fast! If we're not in your area yet, sign up anyway — we'll notify you about your local connections."
    },
    {
      id: 7,
      icon: Heart,
      question: "Can I donate food?",
      answer: "Yes, of course you can. You can buy food for someone else in any part of the world and organise the delivery as well. You could be sat on a beach in the Bahamas, and order food for a hungry family in Africa."
    },
    {
      id: 8,
      icon: Gift,
      question: "Can I get free food?",
      answer: "Yes, of course you can. Once you subscribe, you will be able to raise your hands looking for food and the cooks in your local community and generous individuals globally can notify you of the free food availability. You can either collect your free food yourself from the cook or the delivery person organised by the donator (Philanthropists)."
    },
    {
      id: 9,
      icon: Star,
      question: "What is the best part of this platform?",
      answer: "The best part is by paying just one subscription fee, I can cook a bit extra daily and make extra cash, buy my favourite foods online, donate my extra food and also make additional cash by picking up deliveries. This way I can convert my free time into cash and get richer instead of watching television and suffering poverty."
    }
  ];

  const toggleQuestion = (id: number) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="hero-content2">
          <h1 className="hero-title2">Frequently Asked Questions</h1>
          <p className="hero-subtitle2">
            Everything you need to know about HomeCookt. Can't find what you're looking for? 
            <a href="mailto:Homecookt.com@gmail.com" className="contact-link">Get in touch</a>.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <div className="faq-list">
            {faqs.map((faq) => {
              const IconComponent = faq.icon;
              const isOpen = openQuestion === faq.id;
              
              return (
                <div 
                  key={faq.id} 
                  className={`faq-item ${isOpen ? 'open' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => toggleQuestion(faq.id)}
                    aria-expanded={isOpen}
                  >
                    <div className="question-content">
                      <div className="question-icon">
                        <IconComponent size={24} />
                      </div>
                      <span className="question-text">{faq.question}</span>
                    </div>
                    <div className="chevron-icon">
                      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </button>
                  
                  <div className={`faq-answer ${isOpen ? 'expanded' : ''}`}>
                    <div className="answer-content">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact Section */}
          <div className="faq-contact">
            <div className="contact-card">
              <h3>Still have questions?</h3>
              <p>We're here to help! Reach out to our friendly support team.</p>
              <a href="mailto:Homecookt.com@gmail.com" className="contact-button" target='blank'>
                <MessageCircle size={20} />
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;